import { useCallback, useMemo } from 'react';

import { useOptimizationWorkloads } from '@cast/split-me/features/workload-optimization/hooks';
import { useCluster } from '@cast/shared-cluster-context-data-access';

import { WORKLOAD_VIEWS, type WorkloadCategory } from '../_constants';
import { buildInventoryWorkloadViewPath } from '../navigation';

type MonitoringWorkloadIdentity = {
  namespace?: string;
  workloadName?: string;
  workloadType?: string;
};

const toIdentityKey = ({
  namespace,
  workloadName,
  workloadType,
}: MonitoringWorkloadIdentity) =>
  [namespace ?? '', workloadType ?? '', workloadName ?? ''].join('|');

export const useInventoryMonitoringWorkloadLink = (
  category: WorkloadCategory
) => {
  const { cluster } = useCluster();
  const { workloads } = useOptimizationWorkloads();

  const workloadIdByIdentity = useMemo(() => {
    const map = new Map<string, string>();

    for (const workload of workloads) {
      map.set(
        toIdentityKey({
          namespace: workload.namespace,
          workloadName: workload.name,
          workloadType: workload.kind,
        }),
        workload.id
      );
    }

    return map;
  }, [workloads]);

  return useCallback(
    (identity: MonitoringWorkloadIdentity) => {
      const workloadId = workloadIdByIdentity.get(toIdentityKey(identity));

      if (!workloadId) {
        return undefined;
      }

      return buildInventoryWorkloadViewPath({
        clusterId: cluster.id,
        workloadId,
        category,
        view: WORKLOAD_VIEWS.DASHBOARD,
      });
    },
    [category, cluster.id, workloadIdByIdentity]
  );
};
